import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'

export type DocumentsHelpVariant = 'settings' | 'appointmentType' | 'event' | 'template'

interface Props {
  variant: DocumentsHelpVariant
}

const summaryKey =
  'Emails never attach files directly — attendees receive a download link. The real file is stored on the event, the template only inserts that link.'

export default function DocumentsHelpAlert({ variant }: Props) {
  const { t } = useTranslation()

  const steps: Record<DocumentsHelpVariant, string[]> = {
    settings: [
      t(
        'Add each document here (e.g. Consent form). The code defines the email placeholder {{docs.consent-form.url}}.',
      ),
      t(
        'Optionally upload a default file on the same row — it pre-fills when you attach this document on appointment types or events.',
      ),
      t(
        'You can still change the file per appointment type or per event. That is where attendees get their download link.',
      ),
    ],
    appointmentType: [
      t('Upload the real file here (PDF, etc.). It becomes the default for every event of this appointment type.'),
      t('When someone books this type, the file is copied to that event automatically.'),
      t('You do not add files in email templates — only a link that points to the file on each event.'),
    ],
    event: [
      t('This is where the actual file lives for this event. Upload or change it here.'),
      t('When a notification email is sent, the system turns this file into a secure download link for the attendee.'),
      t('Email templates only contain that link placeholder — never upload the file in the template editor.'),
      t('Usually inherited from the appointment type. Change here only to override for this one event.'),
    ],
    template: [
      t(
        'Do not upload files here. Click a document link in the list below to insert a placeholder (e.g. {{docs.consent-form.url}}).',
      ),
      t('When the email is sent, that placeholder becomes a download link to the file attached on the event.'),
      t(
        'Make sure the matching file exists on the event (or as a default on its appointment type), otherwise the link will be empty.',
      ),
    ],
  }

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="body2" component="div">
        <strong>{t(summaryKey)}</strong>
        <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {steps[variant].map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Typography>
    </Alert>
  )
}
